import React, { useState, useEffect, useMemo } from "react";
import "./dashboard.css";
import customerIcon from "../../assets/svg/customer-ic.svg";
import invoiceIcon from "../../assets/svg/total-invoice.svg";
import totalAmountIcon from "../../assets/svg/overall-amount.svg";
import paidAmountIcon from "../../assets/svg/paid-amount.svg";
import outstandingIcon from "../../assets/svg/outstanding.svg";
import quotationAmountIcon from "../../assets/svg/outstanding.svg";
import invoiceAmountIcon from "../../assets/svg/total-invoice.svg";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import invoiceGreenIcon from "../../assets/svg/invoice-green-ic.svg";
import collectedIcon from "../../assets/svg/rupee.svg";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import {
  getRevenueCountsApi,
  getQuotationOverviewApi,
  getInvoiceOverviewApi,
  getRevenueOverviewApi,
  getRecentPaymentsApi,
  getTransactionsOverviewApi,
  getPaymentTargetOverviewApi,
  getCustomerPaymentOverviewApi,
  getCountryPaymentOverviewApi,
  getCustomerInvoiceOverviewApi,
  getPaymentMethodsOverviewApi,
} from "./dashboard-api.js";
import filterCalender from "../../assets/svg/filterCalender.svg";
import downloadReport from "../../assets/svg/downloadReport.svg";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
);

// Format a plain number as an Indian-grouped rupee amount, e.g. 5020786 -> "₹50,20,786".
const formatMoney = (value) => {
  const num = Number(value) || 0;
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

// Builds the { period, ...anchor } query params shared by the Transactions
// Overview and Target & Actual Payment Overview endpoints, given the active
// period tab and its anchor date (a specific month for Daily, a year for
// Monthly/Quarterly, the end of a 5-year window for Yearly).
const buildPeriodParams = (period, anchorDate) => {
  const periodKey = period.toLowerCase();
  const params = { period: periodKey };
  if (periodKey === "daily") {
    params.month = `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, "0")}`;
  } else if (periodKey === "yearly") {
    const toYear = anchorDate.getFullYear();
    params.from_year = toYear - 4;
    params.to_year = toYear;
  } else {
    params.year = anchorDate.getFullYear();
  }
  return params;
};

// Formats a chart tooltip's title (e.g. "14 July 2026", "Jun 2026", "Q2 2026",
// "2026") from a bucket label (day number/month abbrev/quarter/year) plus the
// active period and its anchor date.
const getPeriodTooltipTitle = (period, anchorDate, label) => {
  const periodKey = period.toLowerCase();
  if (periodKey === "daily") {
    const monthLabel = anchorDate.toLocaleString("en-US", { month: "long", year: "numeric" });
    return `${label} ${monthLabel}`;
  }
  if (periodKey === "yearly") {
    return label;
  }
  return `${label} ${anchorDate.getFullYear()}`;
};

const QUOTATION_COLORS = {
  Draft: "#7C3AED",
  Approved: "#A78BFA",
  Sent: "#C4B5FD",
  Negotiate: "#DDD6FE",
  "Sales order": "#EDE9FE",
  Rejected: "#F5F3FF",
};

const INVOICE_COLORS = {
  Paid: "#7C3AED",
  Unpaid: "#A78BFA",
  Partially: "#C4B5FD",
  Overdue: "#DDD6FE",
};

// The backend only returns a country name — flags are a display-only nicety
// resolved locally; unmapped countries fall back to a globe icon.
const COUNTRY_FLAGS = {
  India: "🇮🇳",
  Malaysia: "🇲🇾",
  Canada: "🇨🇦",
  Dubai: "🇦🇪",
  "United Arab Emirates": "🇦🇪",
  "United States": "🇺🇸",
  "United Kingdom": "🇬🇧",
  Argentina: "🇦🇷",
  Singapore: "🇸🇬",
  Australia: "🇦🇺",
};
const getCountryFlag = (country) => COUNTRY_FLAGS[country] || "🌐";

// Payment method icons/colors are assigned generically by position, since
// the actual payment_type names/count vary per tenant (not a fixed
// Bank/Cheque/UPI/Card set) — icon/color are purely cosmetic here.
const PAYMENT_METHOD_STYLES = [
  { iconBg: "#E53E3E" },
  { iconBg: "#F7C12C" },
  { iconBg: "#3469E7" },
  { iconBg: "#249E55" },
  { iconBg: "#7C3AED" },
  { iconBg: "#0EA5E9" },
];

// Maps a customer's aggregate invoice status to the existing badge CSS
// classes (dashboard.css only defines .pending/.paid/.overdue).
const getStatusBadgeClass = (status) => {
  const key = (status || "").toLowerCase();
  if (key === "paid") return "paid";
  if (key === "overdue") return "overdue";
  return "pending"; // Pending, Partially Paid, etc.
};

export const RevenueDashboard = () => {
    const navigate = useNavigate();
  const [transactionPeriod, setTransactionPeriod] = useState("Monthly");
  const [paymentPeriod, setPaymentPeriod] = useState("Monthly");
  const [revenueYear, setRevenueYear] = useState("2025");
  const [vendorTab, setVendorTab] = useState("Top customers");
  // Anchor dates for the two period-tabbed charts (Transactions Overview and
  // Target & Actual Payment Overview). Meaning depends on the active period:
  // for Daily it's the specific month being shown (day navigated via month),
  // for Monthly/Quarterly it's the year, for Yearly it's the end of a
  // 5-year window.
  const [transactionAnchorDate, setTransactionAnchorDate] = useState(() => new Date());
  const [paymentAnchorDate, setPaymentAnchorDate] = useState(() => new Date());

  const [dashboardData, setDashboardData] = useState({
    customers: 0,
    totalInvoices: 0,
    overallSpend: formatMoney(0),
    totalUnpaid: formatMoney(0),
    outstanding: formatMoney(0),
    quotationCount: 0,
    quotationValue: formatMoney(0),
    conversionRate: "0.0%",
    invoiceCount: 0,
    invoiceValue: formatMoney(0),
    fulFilledPercent: "0.0%",
    revenueValue: formatMoney(0),
  });

  const [quotationBreakdown, setQuotationBreakdown] = useState([]);
  const [invoiceBreakdown, setInvoiceBreakdown] = useState([]);
  const [revenueOverview, setRevenueOverview] = useState({ quoted: 0, invoiced: 0, collected: 0 });
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentPaymentsLoading, setRecentPaymentsLoading] = useState(true);
  const [recentPaymentsPerPage, setRecentPaymentsPerPage] = useState(10);
  const [recentPaymentsOffset, setRecentPaymentsOffset] = useState(0);
  const [recentPaymentsSearch, setRecentPaymentsSearch] = useState("");

  const [customerPayments, setCustomerPayments] = useState([]);
  const [customerPaymentsLoading, setCustomerPaymentsLoading] = useState(true);
  const [customerPaymentsPerPage, setCustomerPaymentsPerPage] = useState(10);
  const [customerPaymentsOffset, setCustomerPaymentsOffset] = useState(0);
  const [customerPaymentsSearch, setCustomerPaymentsSearch] = useState("");

  const [transactionData, setTransactionData] = useState([]);
  const [paymentTargetData, setPaymentTargetData] = useState([]);
  const [customerPaymentOverview, setCustomerPaymentOverview] = useState([]);
  const [countryPayments, setCountryPayments] = useState([]);
  // Month shown in the Country Based Payments Overview filter, as a
  // first-of-month Date (e.g. 2026-07-01 for July 2026).
  const [countryPaymentsMonth, setCountryPaymentsMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  // Rolling window of the last 12 months (current month first) for the
  // Country Based Payments Overview month picker.
  const countryMonthOptions = useMemo(() => {
    const now = new Date();
    const options = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        year: d.getFullYear(),
        month: d.getMonth(),
        label: d.toLocaleString("en-US", { month: "long", year: "numeric" }),
      });
    }
    return options;
  }, []);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Counts (customers, invoices, overall/paid/outstanding amounts)
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await getRevenueCountsApi();
        const data = response?.data || {};
        setDashboardData((prev) => ({
          ...prev,
          customers: data.customers || 0,
          totalInvoices: data.total_invoices || 0,
          overallSpend: formatMoney(data.overall_amount),
          totalUnpaid: formatMoney(data.paid_amount),
          outstanding: formatMoney(data.outstanding),
        }));
      } catch (err) {
        console.error("Failed to load revenue counts:", err);
      }
    };
    fetchCounts();
  }, []);

  // Quotation Overview
  useEffect(() => {
    const fetchQuotationOverview = async () => {
      try {
        const response = await getQuotationOverviewApi();
        const data = response?.data || {};
        setDashboardData((prev) => ({
          ...prev,
          quotationCount: data.total_count || 0,
          quotationValue: formatMoney(data.total_value),
          conversionRate: `${(data.conversion_percentage ?? 0).toFixed(1)}%`,
        }));
        setQuotationBreakdown(
          (data.overview || []).map((item) => ({
            name: item.name,
            count: item.count,
            value: formatMoney(item.value),
            color: QUOTATION_COLORS[item.name] || "#C4B5FD",
          })),
        );
      } catch (err) {
        console.error("Failed to load quotation overview:", err);
      }
    };
    fetchQuotationOverview();
  }, []);

  // Invoice Overview
  useEffect(() => {
    const fetchInvoiceOverview = async () => {
      try {
        const response = await getInvoiceOverviewApi();
        const data = response?.data || {};
        setDashboardData((prev) => ({
          ...prev,
          invoiceCount: data.total_count || 0,
          invoiceValue: formatMoney(data.total_value),
          fulFilledPercent: `${(data.full_paid_percentage ?? 0).toFixed(1)}%`,
        }));
        setInvoiceBreakdown(
          (data.overview || []).map((item) => ({
            name: item.name,
            count: item.count,
            value: formatMoney(item.value),
            color: INVOICE_COLORS[item.name] || "#C4B5FD",
          })),
        );
      } catch (err) {
        console.error("Failed to load invoice overview:", err);
      }
    };
    fetchInvoiceOverview();
  }, []);

  // Revenue Overview (Quoted / Invoiced / Collected, by year)
  useEffect(() => {
    const fetchRevenueOverview = async () => {
      try {
        const response = await getRevenueOverviewApi({ year: revenueYear });
        const data = response?.data || {};
        setRevenueOverview({
          quoted: data.quoted || 0,
          invoiced: data.invoiced || 0,
          collected: data.collected || 0,
        });
      } catch (err) {
        console.error("Failed to load revenue overview:", err);
      }
    };
    fetchRevenueOverview();
  }, [revenueYear]);

  // Recent Payments table. Search is server-side (debounced) rather than
  // filtered client-side.
  useEffect(() => {
    const fetchRecentPayments = async () => {
      setRecentPaymentsLoading(true);
      try {
        const params = recentPaymentsSearch.trim() ? { search: recentPaymentsSearch.trim() } : undefined;
        const response = await getRecentPaymentsApi(params);
        const rows = response?.data || [];
        setRecentPayments(
          rows.map((row, idx) => ({
            sNo: idx + 1,
            paymentId: row.payment_id,
            customerName: row.customer_name,
            invoice: row.invoice,
            amount: formatMoney(row.amount),
            paid: formatMoney(row.paid),
            outstanding: formatMoney(row.outstanding),
            date: row.date,
          })),
        );
        setRecentPaymentsOffset(0);
      } catch (err) {
        console.error("Failed to load recent payments:", err);
      } finally {
        setRecentPaymentsLoading(false);
      }
    };
    const timer = setTimeout(fetchRecentPayments, 400);
    return () => clearTimeout(timer);
  }, [recentPaymentsSearch]);

  // Customer Based Payment Overview — per-customer invoice count/total/paid/outstanding/status.
  // Search is server-side (debounced) rather than filtered client-side.
  useEffect(() => {
    const fetchCustomerInvoiceOverview = async () => {
      setCustomerPaymentsLoading(true);
      try {
        const params = customerPaymentsSearch.trim() ? { search: customerPaymentsSearch.trim() } : undefined;
        const response = await getCustomerInvoiceOverviewApi(params);
        const rows = response?.data || [];
        setCustomerPayments(
          rows.map((row, idx) => ({
            sNo: idx + 1,
            customerName: row.customer_name,
            invoice: row.invoice_count,
            totalAmount: formatMoney(row.total_amount),
            paid: formatMoney(row.paid),
            outstanding: formatMoney(row.outstanding),
            status: row.status,
          })),
        );
        setCustomerPaymentsOffset(0);
      } catch (err) {
        console.error("Failed to load customer invoice overview:", err);
      } finally {
        setCustomerPaymentsLoading(false);
      }
    };
    const timer = setTimeout(fetchCustomerInvoiceOverview, 400);
    return () => clearTimeout(timer);
  }, [customerPaymentsSearch]);

  // Transactions Overview — payment count/value, bucketed per the active
  // period tab (Daily/Monthly/Quarterly/Yearly).
  useEffect(() => {
    const fetchTransactionsOverview = async () => {
      try {
        const params = buildPeriodParams(transactionPeriod, transactionAnchorDate);
        const response = await getTransactionsOverviewApi(params);
        const rows = response?.data || [];
        setTransactionData(
          rows.map((row) => ({
            month: row.month,
            value: row.value,
            payments: row.count,
            amount: formatMoney(row.value),
          })),
        );
      } catch (err) {
        console.error("Failed to load transactions overview:", err);
      }
    };
    fetchTransactionsOverview();
  }, [transactionPeriod, transactionAnchorDate]);

  // Target & Actual Payment Overview — Invoiced / Received / Overdue,
  // bucketed per the active period tab (Daily/Monthly/Quarterly/Yearly).
  useEffect(() => {
    const fetchPaymentTargetOverview = async () => {
      try {
        const params = buildPeriodParams(paymentPeriod, paymentAnchorDate);
        const response = await getPaymentTargetOverviewApi(params);
        setPaymentTargetData(response?.data || []);
      } catch (err) {
        console.error("Failed to load payment target overview:", err);
      }
    };
    fetchPaymentTargetOverview();
  }, [paymentPeriod, paymentAnchorDate]);

  // Customer-based transaction overview (top customers by payments received)
  useEffect(() => {
    const fetchCustomerPaymentOverview = async () => {
      try {
        const response = await getCustomerPaymentOverviewApi({ limit: 5 });
        const rows = response?.data || [];
        setCustomerPaymentOverview(
          rows.map((row) => ({ name: row.name, amount: formatMoney(row.amount) })),
        );
      } catch (err) {
        console.error("Failed to load customer payment overview:", err);
      }
    };
    fetchCustomerPaymentOverview();
  }, []);

  // Country-based payments overview — scoped to the selected month.
  useEffect(() => {
    const fetchCountryPaymentOverview = async () => {
      try {
        const year = countryPaymentsMonth.getFullYear();
        const month = countryPaymentsMonth.getMonth();
        const fromDate = new Date(year, month, 1);
        const toDate = new Date(year, month + 1, 0);
        const toIsoDate = (d) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const response = await getCountryPaymentOverviewApi({
          from_date: toIsoDate(fromDate),
          to_date: toIsoDate(toDate),
        });
        const rows = response?.data || [];
        setCountryPayments(
          rows.map((row) => ({
            country: row.country,
            flag: getCountryFlag(row.country),
            paid: formatMoney(row.paid),
            pending: formatMoney(row.pending),
            overdue: formatMoney(row.overdue),
          })),
        );
      } catch (err) {
        console.error("Failed to load country payment overview:", err);
      }
    };
    fetchCountryPaymentOverview();
  }, [countryPaymentsMonth]);

  // Payment Methods — received amount + count per payment type actually used
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await getPaymentMethodsOverviewApi();
        const rows = response?.data || [];
        setPaymentMethods(
          rows.map((row, idx) => ({
            type: row.type,
            iconBg: PAYMENT_METHOD_STYLES[idx % PAYMENT_METHOD_STYLES.length].iconBg,
            amount: formatMoney(row.amount),
            count: row.count,
            amountLabel: "Received amount",
            countLabel: "Payment count",
          })),
        );
      } catch (err) {
        console.error("Failed to load payment methods overview:", err);
      }
    };
    fetchPaymentMethods();
  }, []);

  // Chart.js configuration for Quotation
  const quotationChartData = {
    labels: quotationBreakdown.map((item) => item.name),
    datasets: [
      {
        data: quotationBreakdown.map((item) => item.count),
        backgroundColor: quotationBreakdown.map((item) => item.color),
        borderWidth: 2,
        borderColor: "#ffffff",
        spacing: 2,
      },
    ],
  };

  // Chart.js configuration for Invoice
  const invoiceChartData = {
    labels: invoiceBreakdown.map((item) => item.name),
    datasets: [
      {
        data: invoiceBreakdown.map((item) => item.count),
        backgroundColor: invoiceBreakdown.map((item) => item.color),
        borderWidth: 2,
        borderColor: "#ffffff",
        spacing: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            return context.label + ": " + context.parsed;
          },
        },
      },
    },
  };

  const transactionChartData = {
    labels: transactionData.map((item) => item.month),
    datasets: [
      {
        label: "Transactions",
        data: transactionData.map((item) => item.value),
        backgroundColor: transactionData.map((item, idx) =>
          idx === 6 ? "#7C3AED" : "#DDD6FE",
        ),
        borderRadius: 8,
        barThickness: 40,
      },
    ],
  };

  const transactionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#FFFFFF",
        titleColor: "#1e293b",
        bodyColor: "#64748b",
        borderColor: "#e2e8f0",
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          title: function (context) {
            const idx = context[0].dataIndex;
            const label = transactionData[idx]?.month || "";
            return getPeriodTooltipTitle(transactionPeriod, transactionAnchorDate, label);
          },
          label: function (context) {
            const idx = context.dataIndex;
            return [
              "Payments: " + transactionData[idx].payments,
              "Amount: " + transactionData[idx].amount,
            ];
          },
          labelTextColor: function (context) {
            return context.parsed.y === 0 ? "#64748b" : "#7C3AED";
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#94a3b8",
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: 100000,
        grid: {
          color: "#f1f5f9",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#94a3b8",
          font: {
            size: 12,
          },
          callback: function (value) {
            if (value === 0) return "0";
            if (value >= 1000) return value / 1000 + "k";
            return value;
          },
          stepSize: 20000,
        },
      },
    },
  };


  // Label shown next to a chart's ←/→ arrows for its active period tab.
  const getPeriodAnchorLabel = (period, anchorDate) => {
    const periodKey = period.toLowerCase();
    if (periodKey === "daily") {
      return anchorDate.toLocaleString("en-US", { month: "long", year: "numeric" });
    }
    if (periodKey === "yearly") {
      const toYear = anchorDate.getFullYear();
      return `${toYear - 4} - ${toYear}`;
    }
    return String(anchorDate.getFullYear());
  };

  // Moves a chart's anchor date backward/forward by the unit relevant to its
  // active period: a month for Daily, a year for Monthly/Quarterly (Yearly
  // moves by 5 since it shows a 5-year window).
  const navigatePeriodAnchor = (period, setAnchorDate, direction) => {
    setAnchorDate((prev) => {
      const next = new Date(prev);
      const periodKey = period.toLowerCase();
      if (periodKey === "daily") {
        next.setMonth(next.getMonth() + direction);
      } else if (periodKey === "yearly") {
        next.setFullYear(next.getFullYear() + direction * 5);
      } else {
        next.setFullYear(next.getFullYear() + direction);
      }
      return next;
    });
  };

  const paymentChartData = {
    labels: paymentTargetData.map((item) => item.month),
    datasets: [
      {
        label: "Invoiced",
        data: paymentTargetData.map((item) => item.invoiced),
        backgroundColor: "#7C3AED",
        borderRadius: 6,
        barThickness: 16,
      },
      {
        label: "Received",
        data: paymentTargetData.map((item) => item.received),
        backgroundColor: "#10B981",
        borderRadius: 6,
        barThickness: 16,
      },
      {
        label: "Overdue",
        data: paymentTargetData.map((item) => item.overdue),
        backgroundColor: "#FBBF24",
        borderRadius: 6,
        barThickness: 16,
      },
    ],
  };

  const paymentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#FFFFFF",
        titleColor: "#1e293b",
        bodyColor: "#64748b",
        borderColor: "#e2e8f0",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        callbacks: {
          title: function (context) {
            const idx = context[0].dataIndex;
            const label = paymentTargetData[idx]?.month || "";
            return getPeriodTooltipTitle(paymentPeriod, paymentAnchorDate, label);
          },
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            return label + ": ₹" + (value / 1000).toFixed(2) + "k";
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#94a3b8",
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: 100000,
        grid: {
          color: "#f1f5f9",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#94a3b8",
          font: {
            size: 12,
          },
          callback: function (value) {
            if (value === 0) return "0";
            if (value >= 1000) return value / 1000 + "k";
            return value;
          },
          stepSize: 20000,
        },
      },
    },
  };

  // Search for Recent Payments is server-side (see the fetchRecentPayments
  // effect above) — only pagination happens client-side, over whatever the
  // backend already returned/filtered.
  const recentPaymentsPageCount = Math.max(
    1,
    Math.ceil(recentPayments.length / recentPaymentsPerPage),
  );
  const pagedRecentPayments = recentPayments.slice(
    recentPaymentsOffset * recentPaymentsPerPage,
    recentPaymentsOffset * recentPaymentsPerPage + recentPaymentsPerPage,
  );

  // Search for Customer Based Payment Overview is server-side (see the
  // fetchCustomerInvoiceOverview effect above) — only pagination happens
  // client-side, over whatever the backend already returned/filtered.
  const customerPaymentsPageCount = Math.max(
    1,
    Math.ceil(customerPayments.length / customerPaymentsPerPage),
  );
  const pagedCustomerPayments = customerPayments.slice(
    customerPaymentsOffset * customerPaymentsPerPage,
    customerPaymentsOffset * customerPaymentsPerPage + customerPaymentsPerPage,
  );

  return (
    <div className="revenue-dashboard p-3">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-tabs">
          <button  onClick={() => navigate("/dashboard")} className="dashboard-tab-btn">Leads Hub</button>
          <button  onClick={() => navigate("/revenue")} className="dashboard-tab-btn active">Revenue Overview</button>
          <button onClick={() => navigate("/pipeline")} className="dashboard-tab-btn">Pipeline</button>
          <button onClick={() => navigate("/forecast")} className="dashboard-tab-btn">Forecast</button>
          <button onClick={() => navigate("/next-action")} className="dashboard-tab-btn">Next Actions</button>
          {/* <button  onClick={() => navigate("/salesperson")} className="dashboard-tab-btn">Sales Performance</button> */}
        </div>
        <div className="header-actions">
          {/* <button className="filter-btn">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
            </svg>
            Filter by date range
          </button>
          <button className="download-btn">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
            </svg>
            Download report
          </button> */}
          <button
                      className="bg-transparent rounded-pill d-flex align-items-center gap-2 px-3 fs-14 fw-600 textPurple"
                      style={{ border: "1px solid #6929E2", height: "39.18px" }}
                    >
                      <img src={filterCalender} alt="calender" />
                      Filter by date range
                    </button>
                    <button className="rounded-pill d-flex align-items-center gap-2 px-4 fs-14 fw-600 text-white backgroundPurple border-0" 
                    style={{height: "39.18px"}}>
                      <img src={downloadReport} alt="downloadReport" />
                      Download report
                    </button>
        </div>
      </div>

      {/* Overview Section */}
      <div className="overview-section">
        <h2 className="overview-title">Overview</h2>
        <div className="overview-metrics-grid">
          <div className="overview-metric-card">
            <div className="metric-icon-circle">
              <img src={customerIcon} alt="Customers" />
            </div>
            <div className="metric-label">Customers</div>
            <div className="metric-value">{dashboardData.customers}</div>
          </div>
          <div className="overview-metric-card">
            <div className="metric-icon-circle purple">
              <img src={invoiceIcon} alt="Total Invoices" />
            </div>
            <div className="metric-label">Total Invoices</div>
            <div className="metric-value">{dashboardData.totalInvoices}</div>
          </div>
          <div className="overview-metric-card">
            <div className="metric-icon-circle ">
              <img src={totalAmountIcon} alt="Overall Amount" />
            </div>
            <div className="metric-label">Overall Amount</div>
            <div className="metric-value">{dashboardData.overallSpend}</div>
          </div>
          <div className="overview-metric-card">
            <div className="metric-icon-circle ">
              <img src={paidAmountIcon} alt="Paid Amount" />
            </div>
            <div className="metric-label">Paid Amount</div>
            <div className="metric-value">{dashboardData.totalUnpaid}</div>
          </div>
          <div className="overview-metric-card">
            <div className="metric-icon-circle ">
              <img src={outstandingIcon} alt="Outstanding" />
            </div>
            <div className="metric-label">Outstanding</div>
            <div className="metric-value">{dashboardData.outstanding}</div>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="overview-grid">
        {/* Quotation Overview */}
        <div className="overview-card ">
          <h3 className="overview-title">Quotation Overview</h3>
          <div className="overview-header-section">
            <div className="header-left">
              <div className="header-icon ">
                <img src={quotationAmountIcon} alt="Quotation" />
              </div>
              <div className="header-info">
                <div className="header-label">Total Count</div>
                <div className="header-value">
                  {dashboardData.quotationCount}
                </div>
              </div>
            </div>
            <div className="conversion-badge-new">
              <div className="badge-label">Conversion %</div>
              <div className="badge-value">{dashboardData.conversionRate}</div>
            </div>
          </div>
          <div className="overview-value-section">
            <span className="value-label">Value : </span>
            <span className="value-amount">{dashboardData.quotationValue}</span>
          </div>
          <div className="overview-content-new">
            <div className="donut-chart-new">
              <Doughnut data={quotationChartData} options={chartOptions} />
            </div>
            <div className="breakdown-list-new">
              {quotationBreakdown.map((item, idx) => (
                <div key={idx} className="breakdown-item-new">
                  <div className="item-left">
                    <span
                      className="color-indicator"
                      style={{ backgroundColor: item.color }}
                    ></span>
                    <span className="item-name">{item.name}</span>
                  </div>
                  <div className="item-right">
                    <span className="item-count">{item.count}</span>
                    <span className="item-amount">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Invoice Overview */}
        <div className="overview-card mx-2">
          <h3 className="overview-title">Invoice Overview</h3>
          <div className="overview-header-section">
            <div className="header-left">
              <div className="header-icon ">
                <img src={invoiceAmountIcon} alt="Invoice" />
              </div>
              <div className="header-info">
                <div className="header-label">Total Count</div>
                <div className="header-value">{dashboardData.invoiceCount}</div>
              </div>
            </div>
            <div className="conversion-badge-new">
              <div className="badge-label">Fully Paid %</div>
              <div className="badge-value">{dashboardData.fulFilledPercent}</div>
            </div>
          </div>
          <div className="overview-value-section">
            <span className="value-label">Value : </span>
            <span className="value-amount">{dashboardData.invoiceValue}</span>
          </div>
          <div className="overview-content-new">
            <div className="donut-chart-new">
              <Doughnut data={invoiceChartData} options={chartOptions} />
            </div>
            <div className="breakdown-list-new">
              {invoiceBreakdown.map((item, idx) => (
                <div key={idx} className="breakdown-item-new">
                  <div className="item-left">
                    <span
                      className="color-indicator"
                      style={{ backgroundColor: item.color }}
                    ></span>
                    <span className="item-name">{item.name}</span>
                  </div>
                  <div className="item-right">
                    <span className="item-count">{item.count}</span>
                    <span className="item-amount">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue Overview */}
        <div className="overview-card revenue-card">
          <h2 className="overview-title">Revenue Overview</h2>
          <div className="revenue-content">
            <div className="year-selector">
              <label>Year</label>
              <span className="year-separator">:</span>
              <select
                value={revenueYear}
                onChange={(e) => setRevenueYear(e.target.value)}
              >
                <option>2025</option>
                <option>2024</option>
                <option>2023</option>
              </select>
            </div>
            <div className="revenue-items">
              <div className="revenue-item">
                <div className="revenue-icon-wrapper grey">
                  <img src={quotationAmountIcon} alt="Quoted" />
                </div>
                <div className="revenue-details">
                  <div className="revenue-label">Quoted</div>
                  <div className="revenue-amount">
                    {formatMoney(revenueOverview.quoted)}
                  </div>
                </div>
              </div>
              <div className="revenue-item">
                <div className="revenue-icon-wrapper green">
                  <img src={invoiceGreenIcon} alt="Invoice" />
                </div>
                <div className="revenue-details">
                  <div className="revenue-label">Invoiced</div>
                  <div className="revenue-amount">
                    {formatMoney(revenueOverview.invoiced)}
                  </div>
                </div>
              </div>
              <div className="revenue-item">
                <div className="revenue-icon-wrapper yellow">
                  <img src={collectedIcon} alt="Collected" />
                </div>
                <div className="revenue-details">
                  <div className="revenue-label">Collected</div>
                  <div className="revenue-amount">
                    {formatMoney(revenueOverview.collected)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Overview */}
      <div className="chart-card">
        <div className="card-header">
          <h2 className="overview-title">Transactions Overview</h2>
          <div className="period-tabs">
            <button
              className={transactionPeriod === "Daily" ? "active" : ""}
              onClick={() => setTransactionPeriod("Daily")}
            >
              Daily
            </button>
            <button
              className={transactionPeriod === "Monthly" ? "active" : ""}
              onClick={() => setTransactionPeriod("Monthly")}
            >
              Monthly
            </button>
            <button
              className={transactionPeriod === "Quarterly" ? "active" : ""}
              onClick={() => setTransactionPeriod("Quarterly")}
            >
              Quarterly
            </button>
            <button
              className={transactionPeriod === "Yearly" ? "active" : ""}
              onClick={() => setTransactionPeriod("Yearly")}
            >
              Yearly
            </button>
          </div>
          <div className="month-nav">
            <span>{getPeriodAnchorLabel(transactionPeriod, transactionAnchorDate)}</span>
            <div className="nav-arrows">
              <button onClick={() => navigatePeriodAnchor(transactionPeriod, setTransactionAnchorDate, -1)}>←</button>
              <button onClick={() => navigatePeriodAnchor(transactionPeriod, setTransactionAnchorDate, 1)}>→</button>
            </div>
          </div>
        </div>
        <div className="chart-content">
          <div className="bar-chart-container">
            <Bar
              data={transactionChartData}
              options={transactionChartOptions}
            />
          </div>
        </div>
      </div>

      {/* Target & Actual Payment Overview */}
      <div className="chart-card">
        <div className="card-header">
          <h2 className="overview-title">Target & Actual Payment Overview</h2>
          <div className="period-tabs">
            <button
              className={paymentPeriod === "Daily" ? "active" : ""}
              onClick={() => setPaymentPeriod("Daily")}
            >
              Daily
            </button>
            <button
              className={paymentPeriod === "Monthly" ? "active" : ""}
              onClick={() => setPaymentPeriod("Monthly")}
            >
              Monthly
            </button>
            <button
              className={paymentPeriod === "Quarterly" ? "active" : ""}
              onClick={() => setPaymentPeriod("Quarterly")}
            >
              Quarterly
            </button>
            <button
              className={paymentPeriod === "Yearly" ? "active" : ""}
              onClick={() => setPaymentPeriod("Yearly")}
            >
              Yearly
            </button>
          </div>
          <div className="month-nav">
            <span>{getPeriodAnchorLabel(paymentPeriod, paymentAnchorDate)}</span>
            <div className="nav-arrows">
              <button onClick={() => navigatePeriodAnchor(paymentPeriod, setPaymentAnchorDate, -1)}>←</button>
              <button onClick={() => navigatePeriodAnchor(paymentPeriod, setPaymentAnchorDate, 1)}>→</button>
            </div>
          </div>
        </div>
        <div className="chart-content">
          <div className="legend">
            <span>
              <span
                className="legend-dot"
                style={{ backgroundColor: "#7C3AED" }}
              ></span>{" "}
              Invoiced
            </span>
            <span>
              <span
                className="legend-dot"
                style={{ backgroundColor: "#10B981" }}
              ></span>{" "}
              Received
            </span>
            <span>
              <span
                className="legend-dot"
                style={{ backgroundColor: "#FBBF24" }}
              ></span>{" "}
              Overdue
            </span>
          </div>
          <div className="bar-chart-container">
            <Bar data={paymentChartData} options={paymentChartOptions} />
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="payment-methods-grid">
        {paymentMethods.length === 0 ? (
          <p style={{ textAlign: "center", padding: "20px", color: "#8790A1" }}>
            No payment methods found.
          </p>
        ) : (
          paymentMethods.map((method, idx) => (
            <div key={idx} className="payment-method-card">
              <div className="method-header">
                <span
                  className="method-icon-new"
                  style={{
                    backgroundColor: method.iconBg,
                  }}
                >
                  <img src={collectedIcon} alt={method.type} />
                </span>
                <span className="method-name">{method.type}</span>
              </div>
              <div className="method-content">
                <div className="method-item">
                  <div className="method-value">{method.amount}</div>
                  <div className="method-label">{method.amountLabel}</div>
                </div>
                <div className="method-item">
                  <div className="method-value method-value-count">
                    {method.count}
                  </div>
                  <div className="method-label">{method.countLabel}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Customer Transaction Overview */}
      <div className="two-col-grid">
        {/* Customer Overview */}
        <div className="table-card">
          <div className="card-header">
            <h3 className="overview-title">
              {" "}
              Customer based transaction overview
            </h3>
          </div>
          <div className="vendor-tabs">
            <button
              className={vendorTab === "Top customers" ? "active" : ""}
              onClick={() => setVendorTab("Top customers")}
            >
              Top customers
            </button>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th className="column1">Customers</th>
                <th className="column2">Paid Amount</th>
              </tr>
            </thead>
            <tbody>
              {customerPaymentOverview.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ textAlign: "center", padding: "20px" }}>
                    No customer payments found.
                  </td>
                </tr>
              ) : (
                customerPaymentOverview.map((vendor, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="customer-cell">
                        <span className="avatar">👤</span>
                        <span>{vendor.name}</span>
                      </div>
                    </td>
                    <td className="paid-amount">{vendor.amount}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Country Payments */}
        <div className="table-card country-payments-card">
          <div className="card-header">
            <h2 className="overview-title">Country based payments overview</h2>
            <select
              className="country-year-select"
              value={`${countryPaymentsMonth.getFullYear()}-${countryPaymentsMonth.getMonth()}`}
              onChange={(e) => {
                const [year, month] = e.target.value.split("-").map(Number);
                setCountryPaymentsMonth(new Date(year, month, 1));
              }}
            >
              {countryMonthOptions.map((opt) => (
                <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="country-payments-list">
            {countryPayments.length === 0 && (
              <p style={{ textAlign: "center", padding: "20px", color: "#8790A1" }}>
                No country-wise payments found.
              </p>
            )}
            {countryPayments.map((country, idx) => (
              <div key={idx} className="country-payment-item">
                <div className="country-info">
                  <div className="country-flag-circle">
                    <span className="flag-emoji">{country.flag}</span>
                  </div>
                  <span className="country-name">{country.country}</span>
                </div>
                <div className="country-amounts">
                  <div className="amount-item">
                    <div className="amount-label">Paid</div>
                    <div className="amount-value">{country.paid}</div>
                  </div>
                  <div className="amount-item">
                    <div className="amount-label">Pending</div>
                    <div className="amount-value">{country.pending}</div>
                  </div>
                  <div className="amount-item">
                    <div className="amount-label">Overdue</div>
                    <div className="amount-value">{country.overdue}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Payments */}
      <div className="table-card full-width">
        <div className="card-header">
          <h2 className="overview-title">Recent Payments</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Search"
              value={recentPaymentsSearch}
              onChange={(e) => {
                setRecentPaymentsSearch(e.target.value);
                setRecentPaymentsOffset(0);
              }}
            />
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>S. No</th>
              <th>
                Payment ID <span>⬍</span>
              </th>
              <th>
                Customer Name <span>⬍</span>
              </th>
              <th>
                Invoice <span>⬍</span>
              </th>
              <th>
                Amount <span>⬍</span>
              </th>
              <th>
                Paid <span>⬍</span>
              </th>
              <th>
                Outstanding <span>⬍</span>
              </th>
              <th>
                Date <span>⬍</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {recentPaymentsLoading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>
                  Loading...
                </td>
              </tr>
            ) : pagedRecentPayments.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>
                  {recentPaymentsSearch.trim() ? "No matching payments found." : "No recent payments found."}
                </td>
              </tr>
            ) : (
              pagedRecentPayments.map((payment, idx) => (
                <tr key={idx}>
                  <td>{payment.sNo}</td>
                  <td>{payment.paymentId}</td>
                  <td>
                    <div className="customer-cell">
                      <span className="avatar">👤</span>
                      <span>{payment.customerName}</span>
                    </div>
                  </td>
                  <td>{payment.invoice}</td>
                  <td>{payment.amount}</td>
                  <td>{payment.paid}</td>
                  <td>{payment.outstanding}</td>
                  <td>{payment.date}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {recentPayments.length > 0 && (
          <div className="d-flex align-items-center justify-content-between mt-3">
            <RecordsPerPage
              value={recentPaymentsPerPage}
              onChange={(event) => {
                setRecentPaymentsPerPage(Number(event.target.value));
                setRecentPaymentsOffset(0);
              }}
              options={[10, 20, 50]}
            />
            <ReactPaginate
              previousLabel={<img src={lar} className="ar3" alt="previous" />}
              nextLabel={<img src={rar} className="ar3" alt="next" />}
              breakLabel={"..."}
              pageCount={recentPaymentsPageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={(event) => setRecentPaymentsOffset(event.selected)}
              forcePage={recentPaymentsOffset}
              key={recentPaymentsOffset}
              containerClassName={"pagination"}
              activeClassName={"active"}
            />
          </div>
        )}
      </div>

      {/* Customer Based Payment Overview */}
      <div className="table-card full-width">
        <div className="card-header">
          <h2 className="overview-title">Customer Based Payment Overview</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Search"
              value={customerPaymentsSearch}
              onChange={(e) => {
                setCustomerPaymentsSearch(e.target.value);
                setCustomerPaymentsOffset(0);
              }}
            />
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>S. No</th>
              <th>
                Customer Name <span>⬍</span>
              </th>
              <th>
                Invoice <span>⬍</span>
              </th>
              <th>
                Total Amount <span>⬍</span>
              </th>
              <th>
                Paid <span>⬍</span>
              </th>
              <th>
                Outstanding <span>⬍</span>
              </th>
              <th>
                Status <span>⬍</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {customerPaymentsLoading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                  Loading...
                </td>
              </tr>
            ) : pagedCustomerPayments.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                  {customerPaymentsSearch.trim() ? "No matching customers found." : "No customer payments found."}
                </td>
              </tr>
            ) : (
              pagedCustomerPayments.map((payment, idx) => (
                <tr key={idx}>
                  <td>{payment.sNo}</td>
                  <td>
                    <div className="customer-cell">
                      <span className="avatar">👤</span>
                      <span>{payment.customerName}</span>
                    </div>
                  </td>
                  <td>{payment.invoice}</td>
                  <td>{payment.totalAmount}</td>
                  <td>{payment.paid}</td>
                  <td>{payment.outstanding}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(payment.status)}`}>
                      {payment.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {customerPayments.length > 0 && (
          <div className="d-flex align-items-center justify-content-between mt-3">
            <RecordsPerPage
              value={customerPaymentsPerPage}
              onChange={(event) => {
                setCustomerPaymentsPerPage(Number(event.target.value));
                setCustomerPaymentsOffset(0);
              }}
              options={[10, 20, 50]}
            />
            <ReactPaginate
              previousLabel={<img src={lar} className="ar3" alt="previous" />}
              nextLabel={<img src={rar} className="ar3" alt="next" />}
              breakLabel={"..."}
              pageCount={customerPaymentsPageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={(event) => setCustomerPaymentsOffset(event.selected)}
              forcePage={customerPaymentsOffset}
              key={customerPaymentsOffset}
              containerClassName={"pagination"}
              activeClassName={"active"}
            />
          </div>
        )}
      </div>
    </div>
  );
};
